import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, Info, AlertTriangle, CheckCircle2, AlertCircle } from 'lucide-react';

const TYPE_OPTIONS = [
  { value: 'info', label: 'Info (blue)', icon: Info, color: 'text-blue-600' },
  { value: 'warning', label: 'Warning (amber)', icon: AlertTriangle, color: 'text-amber-600' },
  { value: 'success', label: 'Success (green)', icon: CheckCircle2, color: 'text-emerald-600' },
  { value: 'error', label: 'Error (red)', icon: AlertCircle, color: 'text-rose-600' },
];

const AUDIENCE_OPTIONS = [
  { value: 'all_staff', label: 'All CRM Staff (staff + admins)' },
  { value: 'admin', label: 'Admins only' },
  { value: 'staff', label: 'Non-admin staff only' },
  { value: 'engineer', label: 'Engineers (Engineer App)' },
  { value: 'customer', label: 'Customers (Customer Portal)' },
];

const emptyForm = {
  title: '',
  message: '',
  type: 'info',
  active: true,
  dismissible: true,
  audiences: ['all_staff'],
  starts_at: '',
  ends_at: '',
};

export const BannersSettings = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/banners/admin');
      setBanners(res.data || []);
    } catch (e) {
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setDialog({ open: true, editing: null });
  };

  const openEdit = (banner) => {
    setForm({
      title: banner.title || '',
      message: banner.message || '',
      type: banner.type || 'info',
      active: banner.active !== false,
      dismissible: banner.dismissible !== false,
      audiences: banner.audiences && banner.audiences.length > 0 ? banner.audiences : ['all_staff'],
      starts_at: banner.starts_at ? banner.starts_at.slice(0, 16) : '',
      ends_at: banner.ends_at ? banner.ends_at.slice(0, 16) : '',
    });
    setDialog({ open: true, editing: banner });
  };

  const toggleAudience = (value) => {
    setForm((prev) => {
      const set = new Set(prev.audiences);
      if (set.has(value)) set.delete(value); else set.add(value);
      return { ...prev, audiences: [...set] };
    });
  };

  const toIso = (v) => (v ? new Date(v).toISOString() : null);

  const save = async () => {
    if (!form.message.trim()) {
      toast.error('Message is required');
      return;
    }
    if (!form.audiences || form.audiences.length === 0) {
      toast.error('Select at least one audience');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title || null,
        message: form.message,
        type: form.type,
        active: form.active,
        dismissible: form.dismissible,
        audiences: form.audiences,
        starts_at: toIso(form.starts_at),
        ends_at: toIso(form.ends_at),
      };
      if (dialog.editing) {
        await api.put(`/banners/${dialog.editing.id}`, payload);
        toast.success('Banner updated');
      } else {
        await api.post('/banners', payload);
        toast.success('Banner created');
      }
      setDialog({ open: false, editing: null });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (banner) => {
    try {
      await api.put(`/banners/${banner.id}`, { active: !banner.active });
      load();
    } catch {
      toast.error('Failed to toggle banner');
    }
  };

  const remove = async (banner) => {
    if (!window.confirm(`Delete banner "${banner.title || banner.message.slice(0, 40)}"?`)) return;
    try {
      await api.delete(`/banners/${banner.id}`);
      toast.success('Banner deleted');
      load();
    } catch {
      toast.error('Failed to delete banner');
    }
  };

  const typeMeta = (t) => TYPE_OPTIONS.find((o) => o.value === t) || TYPE_OPTIONS[0];
  const audienceLabel = (v) => (AUDIENCE_OPTIONS.find((o) => o.value === v)?.label || v);

  return (
    <Card className="border-slate-200" data-testid="banners-settings-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg font-semibold font-['Manrope']">CRM Banners</CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Display important messages at the top of the CRM for all users. Great for maintenance, announcements, or policy updates.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-banner-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Banner
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : banners.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Info className="w-10 h-10 mx-auto mb-3" />
            <p>No banners yet. Create one to broadcast a message across the CRM.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {banners.map((b) => {
              const meta = typeMeta(b.type);
              const Icon = meta.icon;
              return (
                <div
                  key={b.id}
                  className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid={`banner-row-${b.id}`}
                >
                  <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${meta.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {b.title && <span className="font-semibold text-slate-900">{b.title}</span>}
                      <Badge variant="outline" className="text-[10px]">{b.type}</Badge>
                      {b.active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 text-[10px]">Inactive</Badge>
                      )}
                      {b.dismissible && <Badge variant="outline" className="text-[10px]">Dismissible</Badge>}
                      {(b.audiences || ['all_staff']).map((a) => (
                        <Badge key={a} className="bg-indigo-100 text-indigo-700 text-[10px]">{audienceLabel(a)}</Badge>
                      ))}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{b.message}</p>
                    <div className="flex gap-4 mt-1 text-xs text-slate-400">
                      {b.starts_at && <span>Starts: {new Date(b.starts_at).toLocaleString()}</span>}
                      {b.ends_at && <span>Ends: {new Date(b.ends_at).toLocaleString()}</span>}
                      {b.created_by && <span>By: {b.created_by}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Switch
                      checked={b.active}
                      onCheckedChange={() => toggleActive(b)}
                      data-testid={`banner-toggle-${b.id}`}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => openEdit(b)}
                      data-testid={`banner-edit-${b.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-rose-600 hover:text-rose-700"
                      onClick={() => remove(b)}
                      data-testid={`banner-delete-${b.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialog.open} onOpenChange={(open) => setDialog({ open, editing: open ? dialog.editing : null })}>
        <DialogContent className="max-w-lg" data-testid="banner-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.editing ? 'Edit Banner' : 'New Banner'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Title (optional)</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Scheduled Maintenance"
                data-testid="banner-title-input"
              />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="The full message shown to users"
                rows={3}
                data-testid="banner-message-input"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="banner-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Audiences *</Label>
              <p className="text-xs text-slate-500 mt-0.5 mb-2">Pick one or more groups that should see this banner.</p>
              <div className="space-y-2 border border-slate-200 rounded-md p-3 bg-slate-50">
                {AUDIENCE_OPTIONS.map((o) => (
                  <label
                    key={o.value}
                    className="flex items-center gap-2 cursor-pointer"
                    data-testid={`audience-option-${o.value}`}
                  >
                    <input
                      type="checkbox"
                      checked={form.audiences.includes(o.value)}
                      onChange={() => toggleAudience(o.value)}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-700">{o.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Starts (optional)</Label>
                <Input
                  type="datetime-local"
                  value={form.starts_at}
                  onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                  data-testid="banner-starts-input"
                />
              </div>
              <div>
                <Label>Ends (optional)</Label>
                <Input
                  type="datetime-local"
                  value={form.ends_at}
                  onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                  data-testid="banner-ends-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="active-switch">Active</Label>
              <Switch
                id="active-switch"
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="banner-active-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="dismissible-switch">Dismissible by users</Label>
              <Switch
                id="dismissible-switch"
                checked={form.dismissible}
                onCheckedChange={(v) => setForm({ ...form, dismissible: v })}
                data-testid="banner-dismissible-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, editing: null })}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="banner-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {dialog.editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
